import { ReactElement, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import StreamPlayer, {
  StreamPlayerType,
} from "../../components/StreamPlayer/StreamPlayer";
import { ErrorState, StreamState, ViewerStore } from "../../types";
import {
  getViewerBroadcastSettings,
  endViewerStreamStatePolling,
  startViewerStreamStatePolling,
  getViewerPlaybackUrl,
  startViewerCountPolling,
  endViewerCountPolling,
  setViewerStreamState,
} from "../../actions";
import { RootState } from "../../reducers";
import StreamOffline from "../../components/StreamOffline/StreamOffline";
import "./View.scss";
import { Loader } from "@screencloud/screencloud-ui-components";
import Toolbar from "../../components/Toolbar/Toolbar";
import ErrorMessage from "../../components/ErrorMessage/ErrorMessage";

export const STREAM_STATE_CHECK_INTERVAL = 10000;
export const VIEWER_COUNT_CHECK_INTERVAL = 30000;
export const MAX_STREAM_STATE_CHECK_COUNT = 100000;

const View = (): ReactElement<{}> => {
  const dispatch = useDispatch();
  const viewer: ViewerStore = useSelector((state: RootState) => state.viewer);

  useEffect(() => {
    return () => {
      dispatch(endViewerStreamStatePolling());
    };
  }, [dispatch]);

  useEffect(() => {
    const { channel, token, streamState } = viewer;
    if (channel !== null) {
      const { screenCloudOrgId, spaceId, channelId, arn } = channel;
      if (token !== null) {
        if (
          channel.ingestEndpoint === undefined &&
          channel.streamKey === undefined
        ) {
          dispatch(
            getViewerBroadcastSettings({
              orgId: screenCloudOrgId,
              spaceId,
              channelId,
            })
          );
          dispatch(
            getViewerPlaybackUrl({
              orgId: screenCloudOrgId,
              spaceId,
              channelId,
            })
          );
        }
      }

      if (streamState === StreamState.Live) {
        dispatch(
          startViewerCountPolling({
            orgId: screenCloudOrgId,
            spaceId,
            channelId,
            arn,
          })
        );
      } else {
        dispatch(endViewerCountPolling());
      }
    }
  }, [viewer, dispatch]);

  useEffect(() => {
    if (viewer.channel !== null) {
      const { screenCloudOrgId, spaceId, channelId } = viewer.channel;

      if (viewer.streamState === StreamState.Unknown) {
        dispatch(
          startViewerStreamStatePolling({
            orgId: screenCloudOrgId,
            spaceId,
            channelId,
          })
        );
      }
    }
  }, [viewer.streamState, viewer.channel, dispatch]);

  if (
    (viewer.loading && viewer.channel === null) ||
    viewer.streamState === StreamState.Unknown
  ) {
    return (
      <div className="loader-container">
        <Loader inline="centered" active size="massive" />
      </div>
    );
  }

  return (
    <div className="view-container">
      <div className="viewer-error">
        {viewer.errors.length > 0 &&
          viewer.errors.map((error) =>
            error.error !== ErrorState.FailedToVerifyPassword ? (
              <ErrorMessage key={error.uuid} error={error} />
            ) : (
              <div />
            )
          )}
      </div>
      <div className="view-inner-container">
        {viewer.channel !== null &&
        viewer.channel.playbackUrl &&
        viewer.streamState === StreamState.Live ? (
          <StreamPlayer
            url={viewer.channel.playbackUrl}
            type={StreamPlayerType.VideoJs}
            onStreamStateChange={(state) =>
              dispatch(setViewerStreamState(state))
            }
          />
        ) : (
          <StreamOffline state={viewer.streamState} />
        )}
        {viewer.channel !== null && (
          <Toolbar
            streamState={viewer.streamState}
            stream={viewer.channel}
            viewerCount={viewer.viewers}
          />
        )}
      </div>
    </div>
  );
};

export default View;
